import React from 'react';
import { Star, ArrowRight, Info, Briefcase, Share2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CATEGORIES } from '../../constants/serviceConstants';

export const ServiceCard = ({
  service,
  provider,
  onBook,
  onProviderClick,
  compact = false,
  hideProvider = false
}) => {
  const navigate = useNavigate();
  // Get category name from categoryId if not directly available
  const categoryName = service.category ||
    CATEGORIES.find(c => c.id === service.categoryId)?.name ||
    'Other';

  return (
    <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col h-full overflow-hidden group ${compact ? 'border-slate-200' : ''}`}>
      <div
        className={`relative ${compact ? 'h-36' : 'h-48'} overflow-hidden flex-shrink-0 cursor-pointer group/img`}
        onClick={() => navigate(`/service/${service.id}`)}
      >
        {service.imageUrl ? (
          <img
            src={service.imageUrl}
            alt={service.title}
            className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
            <span className="text-4xl">🎨</span>
          </div>
        )}
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-slate-700 shadow-sm">
          {categoryName}
        </div>

        {/* Share Button Overlay */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (navigator.share) {
              navigator.share({
                title: service.title,
                text: service.description,
                url: `${window.location.origin}/service/${service.id}`
              });
            } else {
              navigator.clipboard.writeText(`${window.location.origin}/service/${service.id}`);
              alert('Link copied to clipboard!');
            }
          }}
          className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full text-slate-600 hover:text-indigo-600 hover:bg-white shadow-sm opacity-0 group-hover/img:opacity-100 transition-all duration-300"
          title="Share Service"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 flex-grow flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="ml-1 text-sm font-medium text-slate-700">{service.rating || 0}</span>
            <span className="mx-1 text-slate-300">•</span>
            <span className="text-sm text-slate-500">{service.reviews || 0} reviews</span>
          </div>

          <div className="flex items-center relative group/price cursor-help">
            <div className="text-slate-900 font-bold">
              R{service.priceStart}
            </div>
            <Info className="w-3 h-3 text-slate-400 ml-1.5" />

            <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover/price:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10 shadow-lg">
              Starting from
              <div className="absolute top-full right-1.5 border-4 border-transparent border-t-slate-900"></div>
            </div>
          </div>
        </div>

        <h3
          className={`font-bold text-slate-900 mb-2 line-clamp-2 cursor-pointer hover:text-indigo-600 transition-colors ${compact ? 'text-base' : 'text-lg'}`}
          onClick={() => navigate(`/service/${service.id}`)}
        >
          {service.title}
        </h3>
        {!compact && (
          <p className="text-slate-600 text-sm mb-4 line-clamp-2">
            {service.description}
          </p>
        )}

        {/* Tags */}
        {!compact && service.tags && service.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {service.tags.slice(0, 3).map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                {tag}
              </span>
            ))}
            {service.tags.length > 3 && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">
                +{service.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Provider Stats */}
        {!compact && !hideProvider && provider && (
          <div className="flex items-center gap-3 mb-4 text-xs text-slate-500">
            <div className="flex items-center gap-1" title={`Provider Rating: ${provider.rating || 0}`}>
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="font-medium text-slate-700">{provider.rating || 0}</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-slate-300"></div>
            <div className="flex items-center gap-1" title={`${provider.jobsCompleted || 0} Jobs Completed`}>
              <Briefcase className="w-3.5 h-3.5 text-slate-400" />
              <span className="font-medium text-slate-700">{provider.jobsCompleted || 0}</span>
              <span>Jobs</span>
            </div>
          </div>
        )}

        <div className={`pt-4 border-t border-slate-100 mt-auto flex items-center justify-between gap-4 ${compact ? 'pt-3' : ''}`}>
          {provider && !compact && !hideProvider ? (
            <div
              className="flex items-center gap-2 cursor-pointer group/provider"
              onClick={(e) => {
                e.stopPropagation();
                onProviderClick && onProviderClick(provider.id);
              }}
            >
              {provider.avatarUrl ? (
                <img src={provider.avatarUrl} alt={provider.name} className="w-8 h-8 rounded-full bg-slate-100 object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">
                  {provider.name?.charAt(0) || '?'}
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-xs text-slate-500 font-medium">By</span>
                <span className="text-xs font-bold text-slate-700 group-hover/provider:text-indigo-600 truncate max-w-[100px]">{provider.name}</span>
              </div>
            </div>
          ) : (
            !compact && !hideProvider ? <div /> : null
          )}

          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={() => navigate(`/service/${service.id}`)}
              className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"
              title="View Details"
            >
              <Info className="w-5 h-5" />
            </button>
            <button
              onClick={() => onBook(service)}
              className={`flex items-center bg-indigo-50 text-indigo-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-indigo-600 hover:text-white transition-all`}
            >
              Book <ArrowRight className="ml-1.5 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};